import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DashboardDataService } from './dashboard.data.service';
import { Dashboard, Widget, UserWidget } from './dashboard.model';
import { GridsterApi, GridsterConfig, GridsterItemConfig } from 'angular-gridster2';
import { ToastrService } from 'ngx-toastr';
import { UpgradeNavigationServiceHandler } from '../Common/Routing/upgrade-navigation-handler.service';
import {
    MessageBoxService,
    MessageBoxButtons,
    MessageBoxResult,
} from '../UI-Lib/Message-Box/messagebox.service.upgrade';
import { Subscription } from 'rxjs';
import _ from 'lodash';

@Component({
    selector: 'dashboard',
    template: `
        @if (dashboard) {
            <div style="height: 100%; display: flex; flex-flow: column; gap: 5px;">
                @if (dashboard.firstName) {
                    <div
                        style="flex: 0 1 auto; padding: 10px; display: flex; justify-content: space-between; align-items: center;"
                    >
                        <div style="display: flex; align-items: center;">
                            <h1 style="margin: 0">Welcome {{ dashboard.firstName }}!</h1>
                            <div
                                class="btn-group"
                                [class.invisible]="!designMode"
                                dropdown
                                style="margin-left: 30px; display: flex; gap: 10px;"
                                #addWidgetDropdown="bs-dropdown"
                            >
                                <button
                                    dropdownToggle
                                    type="button"
                                    [disabled]="loading"
                                    class="ace-btn btn-primary dropdown-toggle"
                                >
                                    Add Widget <span class="caret" style="margin-left: 8px;"></span>
                                </button>
                                <ul *dropdownMenu class="dropdown-menu" role="menu">
                                    @for (widget of dashboard.availableWidgets; track widget) {
                                        <li role="menuitem">
                                            <a
                                                class="dropdown-item"
                                                (click)="addWidget(widget)"
                                                style="cursor: pointer;"
                                                >{{ widget.title }}</a
                                            >
                                        </li>
                                    }
                                </ul>
                                <button
                                    type="button"
                                    class="ace-btn btn-primary"
                                    (click)="resetToDefault()"
                                    [disabled]="loading"
                                >
                                    Reset To Default
                                </button>
                            </div>
                        </div>
                        <div>
                            <label style="margin-right: 50px;">
                                Show dashboard on login
                                <ws-switch
                                    style="margin-left: 10px;"
                                    [(ngModel)]="isShowDashboard"
                                    (selectedOptionChanged)="toggleShowDashboard()"
                                ></ws-switch>
                            </label>
                            <label>
                                Design Mode
                                <ws-switch
                                    style="margin-left: 10px;"
                                    [(ngModel)]="designMode"
                                    (selectedOptionChanged)="toggleDesignMode()"
                                ></ws-switch>
                            </label>
                        </div>
                    </div>
                }
                @if (loading) {
                    <i
                        class="fa fa-spinner fa-pulse fa-5x"
                        style="position: absolute; top: 35%; left: 43%; z-index: 2;"
                    ></i>
                }
                <gridster
                    [options]="options"
                    style="background-color: var(--ace-neutral-200);"
                    [style.opacity]="loading ? 0.5 : 1"
                >
                    @for (userWidget of dashboard.userWidgets; track userWidget) {
                        <gridster-item [item]="userWidget">
                            <user-widget
                                [userWidget]="userWidget"
                                (widgetRemove)="removeWidget($event)"
                                [designMode]="designMode"
                            ></user-widget>
                        </gridster-item>
                    }
                </gridster>
            </div>
        }
    `,
    styleUrls: ['./dashboard.component.scss'],
    standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _dashboardDataService: DashboardDataService,
        private readonly _toastr: ToastrService,
        private readonly _upgradeNavigationServiceHandler: UpgradeNavigationServiceHandler,
        private readonly _messageBoxService: MessageBoxService,
    ) {}

    @ViewChild('addWidgetDropdown') addWidgetDropdown;
    dashboard: Dashboard = new Dashboard();
    options: GridsterConfig;
    designMode: boolean = false;
    loading: boolean = false;
    isShowDashboard: boolean = false;
    IAMactive: boolean = false;
    resizeGridSub: Subscription;

    private _gridsterApi: GridsterApi;

    async ngOnInit(): Promise<void> {
        this.options = {
            draggable: {},
            resizable: {},

            margin: 5,
            displayGrid: this.designMode ? 'always' : 'none',
            enableEmptyCellClick: this.designMode,

            maxCols: 4,
            minCols: 4,
            minRows: 3,
            maxRows: 3,
            // pushItems: true,
            scrollToNewItems: true,
            initCallback: (_gridster, api) => {
                this._gridsterApi = api;
            },
            emptyCellClickCallback: () => {
                // this.addWidgetDropdown.show()
            },
            itemResizeCallback: (item: UserWidget) => {
                this._dashboardDataService.userWidgetResize$.next(item.userWidgetId);
            },
            itemChangeCallback: async (userWidget: UserWidget) => {
                this.loading = true;

                try {
                    await this._dashboardDataService.updateUserWidget(userWidget);
                } finally {
                    this.loading = false;
                }
            },
        };

        this.loading = true;

        try {
            this.setDashboard(await this._dashboardDataService.getDashboard());
        } finally {
            this.loading = false;
        }

        this.isShowDashboard = this._dashboardDataService.getShowDashBoardUserSetting();
        this.resizeGridSub = this._dashboardDataService.resizeGrid$.subscribe(() => this._gridsterApi?.resize());
    }

    ngOnDestroy(): void {
        this.resizeGridSub.unsubscribe();
    }

    async addWidget(widget: Widget): Promise<void> {
        const possibleGridItem: GridsterItemConfig = { rows: widget.height, cols: widget.width, x: 0, y: 0 };
        const firstPossiblePosition: GridsterItemConfig = this._gridsterApi.getFirstPossiblePosition(possibleGridItem);

        if (firstPossiblePosition.x + firstPossiblePosition.cols > this.options.maxCols ||
            firstPossiblePosition.y + firstPossiblePosition.rows > this.options.maxRows) {
            this._toastr.error('Not enough room to fit widget!');

            return;
        }

        this.loading = true;

        try {
            const dashboard: Dashboard = await this._dashboardDataService.addToDashboard(
                widget.widgetId,
                firstPossiblePosition,
            );

            this.dashboard.availableWidgets = dashboard.availableWidgets;
            const newUserWidgetIdArr: number[] = _.chain(dashboard.userWidgets)
                .map('userWidgetId')
                .difference(_.map(this.dashboard.userWidgets, 'userWidgetId'))
                .value();

            const newUserWidget: UserWidget = _.find(dashboard.userWidgets, { userWidgetId: newUserWidgetIdArr[0] });
            newUserWidget.minItemCols = newUserWidget.widget.width;
            newUserWidget.minItemRows = newUserWidget.widget.height;

            this.dashboard.userWidgets.push(newUserWidget);
        } finally {
            this.loading = false;
        }
    }

    async removeWidget(userWidgetId: any): Promise<void> {
        userWidgetId = userWidgetId as number;

        this.loading = true;

        try {
            const dashboard: Dashboard = await this._dashboardDataService.removeFromDashboard(userWidgetId);

            this.dashboard.availableWidgets = dashboard.availableWidgets;

            const removedIdx: number = _.findIndex(this.dashboard.userWidgets, { userWidgetId: userWidgetId });
            this.dashboard.userWidgets.splice(removedIdx, 1);
        } finally {
            this.loading = false;
        }
    }

    toggleDesignMode(): void {
        if (!this.designMode && !this.dashboard.userWidgets.length) {
            this._upgradeNavigationServiceHandler.transitionTo(
                this._upgradeNavigationServiceHandler.getCurrentState(),
                {},
                {
                    reload: true,
                },
            );
        }

        this.options = {
            ...this.options,
            draggable: { ...this.options.draggable, enabled: this.designMode },
            resizable: { ...this.options.resizable, enabled: this.designMode },
            displayGrid: this.designMode ? 'always' : 'none',
            enableEmptyCellClick: this.designMode,
        };

        this._gridsterApi?.calculateLayout();
    }

    async resetToDefault(): Promise<void> {
        if (
            (await this._messageBoxService.open({
                buttons: MessageBoxButtons.YesNo,
                message: 'This will delete all of your dashboard settings; are you sure?',
                title: 'Confirm resetting dashboard to defaults',
            })) === MessageBoxResult.Yes
        ) {
            this.loading = true;

            try {
                this.setDashboard(await this._dashboardDataService.resetDashboard());
                this.designMode = false;
            } finally {
                this.loading = false;
            }
        }
    }

    async toggleShowDashboard(): Promise<void> {
        await this._dashboardDataService.updateShowDashboardUserSetting(this.isShowDashboard);
    }

    private setDashboard(dashboard: Dashboard): void {
        dashboard.userWidgets = _.map(dashboard.userWidgets, (userWidget: UserWidget) => {
            userWidget.rows = userWidget.rows || 1;
            userWidget.cols = userWidget.cols || 1;

            userWidget.minItemCols = userWidget.widget.width;
            userWidget.minItemRows = userWidget.widget.height;

            if (userWidget.widget.widgetId === 4) {
                userWidget.useLightIcon = true;
            }

            return userWidget;
        });

        this.dashboard = dashboard;
    }
}
